import React from 'react'
import { Button, Table as AntTable } from 'antd'
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft'
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import EmptyList from 'modules/commons/EmptyList'
import styles from './Table.module.scss'

interface ITable {
  columns: any[]
  dataSource: any[]
  scroll?: any
  responsive?: boolean
  onRowSelected: any
  selectedRowsKeys: any[]
  setSelectedRowsKeys: any
  loading?: boolean
  onSortChange?: any
  showScrollButtons?: boolean
  allSelected: boolean
  unselectedRowsKeys: any[]
  setUnselectedRowsKeys: any
  customEmpty?: React.ReactNode
  replaceTableComponent?: React.ReactNode
  emptyListMessage?: string
  rowSelectionType?: 'checkbox' | 'radio' | undefined
}

const Table: React.FC<ITable> = ({
  columns = [],
  dataSource = [],
  loading = false,
  scroll = undefined,
  responsive = false,
  onRowSelected,
  selectedRowsKeys = [],
  onSortChange,
  setSelectedRowsKeys,
  showScrollButtons = false,
  allSelected = false,
  unselectedRowsKeys = [],
  setUnselectedRowsKeys,
  customEmpty,
  replaceTableComponent,
  emptyListMessage,
  rowSelectionType = 'checkbox',
}) => {
  const tableId = React.useRef(Math.random().toString(36).substr(2, 9))

  const handleAllSelectedLogic = (currentRow: any, originalArray: number[]) => {
    const index = originalArray.findIndex((row: number) => row === currentRow.key)
    const aux = originalArray.slice()
    if (index >= 0) {
      aux.splice(index, 1)
    } else {
      aux.push(currentRow.key)
    }
    return aux
  }
  const rowSelection = {
    selectedRowKeys: selectedRowsKeys,
    onChange: (currentSelectedRowKeys: any, selectedRows: any) => {
      if (!allSelected) {
        setSelectedRowsKeys(currentSelectedRowKeys)
        onRowSelected && onRowSelected({ selectedRowKeys: currentSelectedRowKeys, selectedRows, allSelected: false, unselectedRowKeys: [], dispatcher: 'onChange' })
      }
    },
    onSelect: (row: any) => {
      if (allSelected) {
        const unselectedRows = handleAllSelectedLogic(row, unselectedRowsKeys)
        setUnselectedRowsKeys(unselectedRows)
        const selectedRows = handleAllSelectedLogic(row, selectedRowsKeys)
        setSelectedRowsKeys(selectedRows)
        onRowSelected && onRowSelected({ selectedRowKeys: selectedRows, selectedRows: [], allSelected: true, unselectedRowKeys: unselectedRows, dispatcher: 'onSelect' })
      }
    },
    onSelectAll: (_selected: any, _selectedRows: any, changeRows: any) => {
      if (allSelected) {
        let auxUnselectedRows = unselectedRowsKeys.slice()
        let auxSelectedRows = selectedRowsKeys.slice()
        changeRows.map((row: any[]) => {
          auxUnselectedRows = handleAllSelectedLogic(row, auxUnselectedRows)
          auxSelectedRows = handleAllSelectedLogic(row, auxSelectedRows)
        })

        setUnselectedRowsKeys(auxUnselectedRows)
        setSelectedRowsKeys(auxSelectedRows)
        onRowSelected && onRowSelected({ selectedRowKeys: auxSelectedRows, selectedRows: [], allSelected: true, unselectedRowKeys: auxUnselectedRows, dispatcher: 'onSelectAll' })
      }
    },
    getCheckboxProps: (row: any) => ({
      disabled: row.disabled || row.checkboxDisabled,
    }),
  }
  const handleScroll = (type: string) => {
    const table = document.getElementById(tableId.current)
    let tableBody: any = table && table.getElementsByClassName('ant-table-body')
    tableBody = tableBody && tableBody[0]
    if (tableBody) tableBody.scrollLeft = tableBody.scrollLeft + (type === 'right' ? 100 : -100)
  }

  return (
    <div id={tableId.current}>
      {replaceTableComponent || (
        <AntTable
          className={`rankmiTable-table ${styles.rankmiTable} ${responsive ? styles.responsive : ''}`}
          columns={columns}
          dataSource={dataSource}
          locale={{ emptyText: customEmpty || <EmptyList loading={loading} locale={emptyListMessage} /> }}
          pagination={false}
          rowClassName={(row: any) => (row.disabled ? 'rankmiTable-table-disabledRow' : '')}
          rowSelection={!!onRowSelected ? { type: rowSelectionType, hideDefaultSelections: true, ...rowSelection } : undefined}
          scroll={scroll}
          // @ts-ignore
          onChange={(pagination, filters, { order, field }: any) => onSortChange && onSortChange({ order, field })}
        />
      )}
      {showScrollButtons && (
        <div className={`${styles.scrollButtons} mt-3`}>
          <Button className="mr-2" onClick={() => handleScroll('left')}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </Button>
          <Button onClick={() => handleScroll('right')}>
            <FontAwesomeIcon icon={faArrowRight} />
          </Button>
        </div>
      )}
    </div>
  )
}

function areEqual(
  {
    columns: prevColumns = [],
    dataSource: prevDataSource = [],
    selectedRowsKeys: prevSelectedRowsKeys = [],
    loading: prevLoading,
    showScrollButtons: prevShowScrollButtons = false,
    responsive: prevResponsive,
    allSelected: prevAllSelected,
    unselectedRowsKeys: prevUnselectedRowsKeys,
  }: any,
  { columns = [], dataSource = [], selectedRowsKeys = [], loading = false, showScrollButtons = false, responsive = false, allSelected = false, unselectedRowsKeys = [] }: any
) {
  if (prevResponsive !== responsive) return false
  if (prevShowScrollButtons !== showScrollButtons) return false
  if (prevColumns !== columns) return false
  if (prevDataSource !== dataSource) return false
  if (prevSelectedRowsKeys !== selectedRowsKeys) return JSON.stringify(prevSelectedRowsKeys) === JSON.stringify(selectedRowsKeys)
  if (prevLoading !== loading) return false
  if (prevUnselectedRowsKeys !== unselectedRowsKeys) return false
  if (prevAllSelected !== allSelected) return false
  return true
}

export default React.memo(Table, areEqual)
